/**
 * Non-hook utility to check if we're viewing a shared thread.
 * Safe to call outside React component lifecycle or before Router is mounted.
 *
 * This is in a separate file from useIsSharedThread.ts to avoid pulling in
 * React/wouter dependencies when only the path check is needed.
 */
export const isSharedThreadPath = () => {
  return typeof window !== "undefined" && window.location.pathname.includes("/shared/");
};

/**
 * The share token from a `/shared/<token>` URL, or null if not on a shared
 * thread. Used to address the public, token-scoped asset endpoints (e.g. image
 * previews) so an unauthenticated viewer can load them.
 *
 * `/shared/a/<token>` — a shared *artifact* — is deliberately excluded. Without
 * the guard this returns the literal `"a"` and `toSharedAssetPath` rewrites
 * assets to `/api/p/shared/a/files/…`, a path that does not exist. A shared
 * artifact's assets need no rewriting: the server writes URLs that authorize the
 * share straight into the card's view — a file's preview, a document's
 * `imageSrcs` — and marks them as not the owner's routes, so null (leave the
 * path alone) is the right answer.
 *
 * `isSharedThreadPath` still matches that URL, which is what we want — the view
 * genuinely is unauthenticated and blocks should suppress session-authed
 * content there.
 */
export const getShareTokenFromPath = (): string | null => {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/\/shared\/(?!a\/)([^/?#]+)/);
  return match?.[1] ?? null;
};

/**
 * Rewrite a session-authed API asset path (`/api/a/files/...`) to its public,
 * share-token-scoped equivalent (`/api/p/shared/<token>/files/...`) so a
 * shared-thread viewer can load it without a session.
 *
 * The `/api` mount is captured and re-emitted rather than matched loosely, so
 * the result keeps whichever form it was given. Dropping it would hand
 * `resolveApiPath` a mount-relative `/p/...`, which still resolves but routes a
 * current-form path down the legacy branch.
 */
export const toSharedAssetPath = (authedPath: string, shareToken: string): string =>
  authedPath.replace(/^(\/api)?\/a\//, `$1/p/shared/${shareToken}/`);
